import { useCallback, useMemo, useEffect, useContext } from 'react';
import { useSelector } from 'react-redux';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import AutoSizer from 'react-virtualized-auto-sizer';
import Raven from 'raven-js';
import { List as VirtualizedList, InfiniteLoader } from 'react-virtualized';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { getLoadMoreThreads } from 'conversations-inbox-lib/usage-tracking/inboxInteractionEvents';
import { CRITICAL_PATH_THREAD_LIST_MEMBERS, SECONDARY_PATH_THREAD_LIST_MEMBERS } from '../../performance-metrics/constants/performanceConstants';
import { useCurrentViewIsProcessing } from '../../custom-views/hooks/useCurrentViewIsProcessing';
import { threadListMemberRowGenerator } from '../operators/threadListMemberRowGenerator';
import { getIncomingThreadId } from '../../performance-metrics/selectors/getIncomingThreadId';
import { useIsAssociatedTicketOpen } from '../../thread-list-member/hooks/useIsAssociatedTicketOpen';
import { NavMarker } from 'react-rhumb';
import { ConversationHandoffErrorContext } from 'conversations-thread-view/move-to-inbox/context/ConversationHandoffErrorContext';
import isEmpty from 'transmute/isEmpty';
import DisplayResultsText from './DisplayResultsText';
import ThreadListMemberSidebarSkeletonState from './ThreadListMemberSidebarSkeletonState';
import { getThreadId } from 'find-and-filter-data/view-members-schema/protected';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
import { isInSearchAndFilterMode as getIsInSearchAndFilterMode } from 'conversations-inbox-lib/thread-search-and-filtering/public/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useMeasureTLMCriticalPath = incomingThreadId => {
  useEffect(() => {
    try {
      performance.measure(CRITICAL_PATH_THREAD_LIST_MEMBERS);
      if (window.newrelic) {
        const entries = performance.getEntriesByName(CRITICAL_PATH_THREAD_LIST_MEMBERS);
        if (entries && entries.length > 0) {
          const duration = entries[0].duration;
          window.newrelic.setCustomAttribute(`${incomingThreadId ? SECONDARY_PATH_THREAD_LIST_MEMBERS : CRITICAL_PATH_THREAD_LIST_MEMBERS}`, duration);
        }
      }
    } catch (error) {
      Raven.captureException(error);
    }
  }, [incomingThreadId]);
  return;
};
const NoRowsComponent = ({
  zeroState,
  sidebarLoadingState,
  searchLoadingState
}) => {
  const isInSearchAndFilterMode = useSelector(getIsInSearchAndFilterMode);
  if (sidebarLoadingState || searchLoadingState) {
    return /*#__PURE__*/_jsx(ThreadListMemberSidebarSkeletonState, {});
  }
  if (zeroState && !isInSearchAndFilterMode) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "THREAD_LIST_ZERO_STATE",
      children: /*#__PURE__*/_jsx(DisplayResultsText, {
        className: "selenium-test-marker-thread-list-zero-state",
        message: "conversations-inbox-ui.chat.noConversations"
      })
    });
  }
  return null;
};
NoRowsComponent.displayName = 'NoRowsComponent';
const VirtualizedThreadListMemberList = ({
  fetchThreadListMembersPage,
  isInitialPageReceived,
  focusedThreadListMember,
  hasMoreThreadListMembers,
  isLoadingThreadListMembers,
  isRefetchingSearchResults,
  onSelectThread = () => {},
  threadListMembers
}) => {
  const {
    threadErrors
  } = useContext(ConversationHandoffErrorContext);
  const isCurrentViewEvaluating = useCurrentViewIsProcessing();
  const incomingThreadId = useSelector(getIncomingThreadId);
  const rowCount = hasMoreThreadListMembers ? threadListMembers.size + 1 : threadListMembers.size;
  const loadMoreRows = useCallback(() => {
    if (isLoadingThreadListMembers) {
      return Promise.resolve();
    }
    fetchThreadListMembersPage();
    trackEvent(getLoadMoreThreads({
      threadListMembers
    }));
    return Promise.resolve();
  }, [fetchThreadListMembersPage, isLoadingThreadListMembers, threadListMembers]);
  const isRowLoaded = useCallback(({
    index
  }) => !!threadListMembers.get(index), [threadListMembers]);
  const focusedThreadId = focusedThreadListMember && getThreadId(focusedThreadListMember);
  const associatedTicketOpen = useIsAssociatedTicketOpen({
    // @ts-expect-error ts-migrate(2322) FIXME: Type 'number | null' is not assignable to type 'st... Remove this comment to see the full error message
    threadId: focusedThreadId
  });
  const moveAndAssignError = !isEmpty(focusedThreadId) ? threadErrors[focusedThreadId] : undefined;
  const isAssociatedTicketOpen = !!associatedTicketOpen;
  const itemData = useMemo(() => ({
    threadListMembers,
    onSelectThread,
    focusedThreadListMember,
    isAssociatedTicketOpen,
    moveAndAssignError
  }), [threadListMembers, onSelectThread, focusedThreadListMember, isAssociatedTicketOpen, moveAndAssignError]);
  const rowRenderer = useMemo(() => threadListMemberRowGenerator(itemData), [itemData]);
  useMeasureTLMCriticalPath(incomingThreadId);
  const zeroState = threadListMembers.size === 0 && !focusedThreadListMember;
  const sidebarLoadingState = isLoadingThreadListMembers && !isInitialPageReceived || isCurrentViewEvaluating;
  const searchLoadingState = isLoadingThreadListMembers && isRefetchingSearchResults;
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    errorLoggingTitle: "VirtualizedThreadListMemberList",
    children: /*#__PURE__*/_jsxs("div", {
      style: {
        flex: '1 1 auto'
      },
      "data-test-id": "VirtualizedThreadListMemberList",
      children: [threadListMembers.count() ? /*#__PURE__*/_jsx(NavMarker, {
        name: "THREAD_LIST_SUCCESS"
      }) : null, /*#__PURE__*/_jsx(InfiniteLoader, {
        isRowLoaded: isRowLoaded,
        loadMoreRows: loadMoreRows,
        rowCount: rowCount,
        children: ({
          onRowsRendered,
          registerChild
        }) => /*#__PURE__*/_jsx(AutoSizer, {
          children: ({
            height,
            width
          }) => /*#__PURE__*/_jsx(VirtualizedList, {
            ref: registerChild,
            width: width,
            height: height,
            rowCount: rowCount,
            rowHeight: 106,
            onRowsRendered: onRowsRendered,
            noRowsRenderer: () => /*#__PURE__*/_jsx(NoRowsComponent, {
              zeroState: zeroState,
              sidebarLoadingState: sidebarLoadingState,
              searchLoadingState: searchLoadingState
            }),
            rowRenderer: rowRenderer,
            minimumBatchSize: 1,
            threshold: 1,
            overscanRowCount: 4,
            focusedThreadListMember: focusedThreadListMember
          })
        })
      })]
    })
  });
};
VirtualizedThreadListMemberList.displayName = 'VirtualizedThreadListMemberList';
export default VirtualizedThreadListMemberList;